import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "outline";
type Size = "sm" | "md" | "icon";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** Show a pending spinner and block repeat clicks. */
  pending?: boolean;
}

const base =
  "inline-flex items-center justify-center gap-1.5 rounded-full font-medium " +
  "transition-[background-color,color,box-shadow,transform] duration-150 ease-out " +
  "active:scale-[0.97] " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 focus-visible:ring-offset-1 focus-visible:ring-offset-bg " +
  "disabled:pointer-events-none disabled:opacity-50 select-none whitespace-nowrap";

const variants: Record<Variant, string> = {
  primary: "bg-accent text-accent-fg shadow-sm hover:shadow hover:brightness-[1.06]",
  secondary: "bg-bg-mute text-fg hover:bg-border",
  ghost: "text-fg-soft hover:bg-bg-mute hover:text-fg",
  outline: "border border-border-strong bg-bg text-fg hover:border-fg-mute/60 hover:bg-bg-soft shadow-sm",
  danger: "bg-danger text-danger-fg shadow-sm hover:shadow hover:brightness-[1.06]",
};

const sizes: Record<Size, string> = {
  sm: "h-8 px-3.5 text-[13px]",
  md: "h-9 px-4 text-sm",
  icon: "h-9 w-9 text-base",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = "primary", size = "md", pending, disabled, children, type = "button", ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(base, variants[variant], sizes[size], className)}
      disabled={disabled || pending}
      aria-busy={pending || undefined}
      {...props}
    >
      {pending && (
        <span
          className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-hidden="true"
        />
      )}
      {children}
    </button>
  );
});
